<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { Menu, X } from 'lucide-vue-next'
import Logo from '../brand/Logo.vue'
import { appLoginUrl } from '../../config'
import { useDevice } from '../../composables/useDevice'

const isScrolled = ref(false)
const isMobileMenuOpen = ref(false)

const { canRunDesktopApp } = useDevice()

const navItems = computed(() => {
  const items: { label: string; href: string }[] = [
    { label: 'How it works', href: '/how-it-works' },
    { label: 'Use cases', href: '/use-cases' },
    { label: 'Pricing', href: '/pricing' },
  ]
  if (canRunDesktopApp.value) items.push({ label: 'Download', href: '/download' })
  return items
})

const signInHref = appLoginUrl()
const signUpHref = appLoginUrl({ mode: 'signup' })

function handleScroll() {
  isScrolled.value = window.scrollY > 20
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') isMobileMenuOpen.value = false
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
  <header
    :class="[
      'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
      isScrolled
        ? 'bg-surface-950/95 backdrop-blur-md border-b border-surface-800 py-2.5 shadow-card'
        : 'bg-transparent py-3.5'
    ]"
  >
    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between gap-3">
        <RouterLink to="/" class="group flex min-h-11 flex-shrink-0 items-center gap-2.5 rounded-control" aria-label="OpenMuse home">
          <Logo class="w-8 h-8" />
          <span class="font-display text-lg font-bold text-surface-50 transition-colors group-hover:text-primary-300">
            OpenMuse
          </span>
        </RouterLink>

        <nav class="hidden items-center gap-1 lg:flex">
          <RouterLink
            v-for="item in navItems"
            :key="item.label"
            :to="item.href"
            class="rounded-control px-4 py-2 text-sm font-medium whitespace-nowrap text-surface-300 transition-colors hover:bg-surface-800 hover:text-surface-50"
            active-class="bg-surface-800 text-primary-300"
          >
            {{ item.label }}
          </RouterLink>
        </nav>

        <div class="hidden items-center gap-2 lg:flex">
          <a
            :href="signInHref"
            class="rounded-control px-3 py-2 text-sm font-medium whitespace-nowrap text-surface-300 transition-colors hover:bg-surface-800 hover:text-surface-50"
          >
            Sign in
          </a>
          <a
            :href="signUpHref"
            class="inline-flex min-h-10 items-center gap-2 rounded-control bg-primary-600 px-5 py-2 text-sm font-semibold whitespace-nowrap text-white shadow-card transition-colors hover:bg-primary-700"
          >
            Get started
          </a>
        </div>

        <div class="flex items-center gap-2 flex-shrink-0 lg:hidden">
          <a
            :href="signUpHref"
            class="inline-flex min-h-11 items-center rounded-control bg-primary-600 px-4 py-2 text-xs font-semibold text-white shadow-card transition-colors hover:bg-primary-700"
          >
            Get started
          </a>
          <button
            type="button"
            @click="isMobileMenuOpen = !isMobileMenuOpen"
            class="inline-flex size-11 items-center justify-center rounded-control text-surface-300 transition-colors hover:bg-surface-800 hover:text-surface-50"
            :aria-label="isMobileMenuOpen ? 'Close menu' : 'Open menu'"
            :aria-expanded="isMobileMenuOpen"
            aria-controls="mobile-navigation"
          >
            <Menu v-if="!isMobileMenuOpen" class="w-5 h-5" />
            <X v-else class="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>

    <div
      id="mobile-navigation"
      v-show="isMobileMenuOpen"
      class="absolute left-0 right-0 top-full border-y border-surface-800 bg-surface-900 shadow-panel lg:hidden"
    >
      <nav class="px-4 py-3 space-y-1">
        <RouterLink
          v-for="item in navItems"
          :key="item.label"
          :to="item.href"
          @click="isMobileMenuOpen = false"
          class="block min-h-11 rounded-control px-4 py-3 text-sm font-medium text-surface-200 transition-colors hover:bg-surface-800 hover:text-primary-300"
        >
          {{ item.label }}
        </RouterLink>
        <a
          :href="signInHref"
          @click="isMobileMenuOpen = false"
          class="block min-h-11 rounded-control px-4 py-3 text-sm font-medium text-surface-200 transition-colors hover:bg-surface-800 hover:text-primary-300"
        >
          Sign in
        </a>
      </nav>
    </div>
  </header>
</template>
